import {EnumDisponibilidadeProduto} from "../lib/emun/EnumDisponibilidadeProduto";

/**
 * Regras puras (sem moment, sem Angular) para decidir em que dias/horários um produto
 * "disponível em dias específicos" pode ser vendido. Usadas pelo servidor (listagem e validação)
 * e pela loja (sacola e agendamento), para as duas pontas concordarem.
 *
 * Formato esperado (JSON do catálogo):
 *   produto.disponibilidades: [{id}]
 *   catalogo.disponibilidades: [{id, dataInicio?, dataFim?, periodos?: [{dias: [{dia}], horaInicio?, horaFim?}]}]
 */
export class RegrasDeAgendamentoDeProduto {
  static readonly NOMES_DOS_DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

  /** Produto com restrição por dia/horário ativa e disponibilidades vinculadas. */
  static temRestricaoDeDia(produto: any): boolean {
    return !!(produto && produto.disponibilidade === EnumDisponibilidadeProduto.DiaDaSemana &&
      produto.disponibilidades && produto.disponibilidades.length);
  }

  /** Disponibilidades do produto resolvidas contra o catálogo (só as que existem nele). */
  static disponibilidadesDoProduto(produto: any, catalogo: any): any[] {
    if (!RegrasDeAgendamentoDeProduto.temRestricaoDeDia(produto)) return [];
    const doCatalogo: any[] = (catalogo && catalogo.disponibilidades) || [];

    return produto.disponibilidades
      .map((vinculo: any) => doCatalogo.find((d: any) => d.id === vinculo.id))
      .filter((d: any) => !!d);
  }

  /** Disponível no dia (ignora horário): sem restrição, ou alguma disponibilidade cobre a data e o dia da semana. */
  static disponivelNoDia(produto: any, catalogo: any, data: Date): boolean {
    if (!RegrasDeAgendamentoDeProduto.temRestricaoDeDia(produto)) return true;

    return RegrasDeAgendamentoDeProduto.disponibilidadesDoProduto(produto, catalogo)
      .some((d: any) => RegrasDeAgendamentoDeProduto.disponibilidadeCobreODia(d, data));
  }

  /** Disponível na data e hora: como o dia, mas períodos com horário precisam conter a hora. */
  static disponivelNaDataHora(produto: any, catalogo: any, dataHora: Date): boolean {
    if (!RegrasDeAgendamentoDeProduto.temRestricaoDeDia(produto)) return true;

    return RegrasDeAgendamentoDeProduto.disponibilidadesDoProduto(produto, catalogo)
      .some((d: any) => RegrasDeAgendamentoDeProduto.disponibilidadeCobreADataHora(d, dataHora));
  }

  /** Dias da semana (0=dom..6=sáb) em que o produto pode ser vendido; null = sem restrição de dia. */
  static diasDaSemanaDisponiveis(produto: any, catalogo: any): number[] | null {
    if (!RegrasDeAgendamentoDeProduto.temRestricaoDeDia(produto)) return null;

    const dias = new Set<number>();

    for (const d of RegrasDeAgendamentoDeProduto.disponibilidadesDoProduto(produto, catalogo)) {
      if (!d.periodos || !d.periodos.length) {
        for (let dia = 0; dia <= 6; dia++) dias.add(dia);
        continue;
      }
      for (const periodo of d.periodos)
        for (const info of (periodo.dias || [])) dias.add(Number(info.dia));
    }

    return Array.from(dias).sort((a, b) => a - b);
  }

  /** Dias em comum entre vários produtos (null em qualquer um = não restringe). Vazio = incompatíveis. */
  static diasEmComum(produtos: any[], catalogo: any): number[] | null {
    let comum: number[] | null = null;

    for (const produto of produtos) {
      const dias = RegrasDeAgendamentoDeProduto.diasDaSemanaDisponiveis(produto, catalogo);
      if (dias == null) continue;
      comum = comum == null ? dias : comum.filter((dia) => dias.indexOf(dia) >= 0);
    }

    return comum;
  }

  /** "aos sábados", "de segunda a sexta", "às terças e quintas", "todos os dias". */
  static descrevaDias(dias: number[]): string {
    if (!dias || !dias.length) return '';
    const unicos = Array.from(new Set(dias)).sort((a, b) => a - b);
    if (unicos.length === 7) return 'todos os dias';

    const nome = (dia: number) => RegrasDeAgendamentoDeProduto.NOMES_DOS_DIAS[dia];
    const artigoPlural = (dia: number) => (dia === 0 || dia === 6) ? 'aos' : 'às';
    const plural = (dia: number) => nome(dia) + 's';

    if (unicos.length === 1) return `${artigoPlural(unicos[0])} ${plural(unicos[0])}`;

    const sequencia = unicos.every((dia, i) => i === 0 || unicos[i - 1] + 1 === dia);
    if (sequencia && unicos.length >= 3)
      return `de ${nome(unicos[0])} a ${nome(unicos[unicos.length - 1])}`;

    const todosFimDeSemana = unicos.every((dia) => dia === 0 || dia === 6);
    const artigo = todosFimDeSemana ? 'aos' : 'às';
    const nomes = unicos.map(plural);

    return `${artigo} ${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1]}`;
  }

  /**
   * Descrição curta para o cliente: "aos sábados", "aos sábados, das 11:00 às 14:00",
   * "de segunda a sexta (até 30/09)". Vazia quando o produto não tem restrição.
   */
  static descreva(produto: any, catalogo: any): string {
    const disponibilidades = RegrasDeAgendamentoDeProduto.disponibilidadesDoProduto(produto, catalogo);
    if (!disponibilidades.length) return '';

    const partes: string[] = [];

    for (const d of disponibilidades) {
      const periodos: any[] = d.periodos || [];
      let texto: string;

      if (!periodos.length) {
        texto = 'todos os dias';
      } else {
        const dias: number[] = [];
        periodos.forEach((p: any) => (p.dias || []).forEach((info: any) => dias.push(Number(info.dia))));
        texto = RegrasDeAgendamentoDeProduto.descrevaDias(dias);

        const comHorario = periodos.filter((p: any) => p.horaInicio && p.horaFim);
        if (comHorario.length === periodos.length && periodos.length === 1)
          texto += `, das ${RegrasDeAgendamentoDeProduto.horaTexto(periodos[0].horaInicio)} às ` +
            `${RegrasDeAgendamentoDeProduto.horaTexto(periodos[0].horaFim)}`;
      }

      const inicio = RegrasDeAgendamentoDeProduto.dataTexto(d.dataInicio);
      const fim = RegrasDeAgendamentoDeProduto.dataTexto(d.dataFim);
      if (inicio && fim) texto += ` (de ${inicio} a ${fim})`;
      else if (inicio) texto += ` (a partir de ${inicio})`;
      else if (fim) texto += ` (até ${fim})`;

      partes.push(texto);
    }

    return partes.join(' ou ');
  }

  static disponibilidadeCobreODia(disponibilidade: any, data: Date): boolean {
    if (!RegrasDeAgendamentoDeProduto.dentroDoIntervaloDeDatas(disponibilidade, data)) return false;

    const periodos: any[] = disponibilidade.periodos || [];
    if (!periodos.length) return true;

    const dia = data.getDay();

    return periodos.some((p: any) => (p.dias || []).some((info: any) => Number(info.dia) === dia));
  }

  static disponibilidadeCobreADataHora(disponibilidade: any, dataHora: Date): boolean {
    if (!RegrasDeAgendamentoDeProduto.dentroDoIntervaloDeDatas(disponibilidade, dataHora)) return false;

    const periodos: any[] = disponibilidade.periodos || [];
    if (!periodos.length) return true;

    const dia = dataHora.getDay();
    const minutos = dataHora.getHours() * 60 + dataHora.getMinutes();

    return periodos.some((p: any) => {
      if (!(p.dias || []).some((info: any) => Number(info.dia) === dia)) return false;
      if (!p.horaInicio || !p.horaFim) return true;

      const inicio = RegrasDeAgendamentoDeProduto.horaEmMinutos(p.horaInicio);
      const fim = RegrasDeAgendamentoDeProduto.horaEmMinutos(p.horaFim);
      if (inicio == null || fim == null) return true;

      return inicio <= minutos && minutos <= fim;
    });
  }

  private static dentroDoIntervaloDeDatas(disponibilidade: any, data: Date): boolean {
    const dia = RegrasDeAgendamentoDeProduto.inicioDoDia(data).getTime();

    if (disponibilidade.dataInicio) {
      const inicio = RegrasDeAgendamentoDeProduto.inicioDoDia(new Date(disponibilidade.dataInicio)).getTime();
      if (dia < inicio) return false;
    }
    if (disponibilidade.dataFim) {
      const fim = RegrasDeAgendamentoDeProduto.inicioDoDia(new Date(disponibilidade.dataFim)).getTime();
      if (dia > fim) return false;
    }

    return true;
  }

  static inicioDoDia(data: Date): Date {
    const copia = new Date(data.getTime());
    copia.setHours(0, 0, 0, 0);
    return copia;
  }

  /** "HH:mm[:ss]" ou Date -> minutos desde a meia-noite. */
  static horaEmMinutos(valor: any): number | null {
    if (!valor) return null;
    if (valor instanceof Date) return valor.getHours() * 60 + valor.getMinutes();

    const partes = String(valor).split(':');
    const horas = Number(partes[0]);
    const minutos = Number(partes[1] || 0);
    if (isNaN(horas) || isNaN(minutos)) return null;

    return horas * 60 + minutos;
  }

  static horaTexto(valor: any): string {
    const minutos = RegrasDeAgendamentoDeProduto.horaEmMinutos(valor);
    if (minutos == null) return '';
    const hh = String(Math.floor(minutos / 60)).padStart(2, '0');
    const mm = String(minutos % 60).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  private static dataTexto(valor: any): string {
    if (!valor) return '';
    const data = valor instanceof Date ? valor : new Date(valor);
    if (isNaN(data.getTime())) return '';
    const dd = String(data.getDate()).padStart(2, '0');
    const mm = String(data.getMonth() + 1).padStart(2, '0');
    return `${dd}/${mm}`;
  }
}
